import {defineMessages, useIntl} from 'react-intl'
import {REGEX} from '../../constants'
import {useXSSProtection} from './../../hooks/use-xss-protection'
import {useToast} from '../../hooks/use-toast'
import useShipToCountry from '../../hooks/use-ship-to-country.js'

const messages = defineMessages({
    zipCode: {defaultMessage: 'Zip Code', id: 'track_my_order_fields.label.zipCode'},
    postalCode: {defaultMessage: 'Postal Code', id: 'track_my_order_fields.label.postal_code'}
})
/**
 * Custom hook that generates form field configurations for a "Do Not Sell" request form.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.form - The form object containing control and formState.
 * @param {Object} params.form.control - The control object used for form management.
 * @param {Object} params.form.formState - The form state object containing errors.
 * @param {Object} params.form.formState.errors - The errors object for form validation.
 * @param {string} [params.prefix=''] - An optional prefix to prepend to field names.
 *
 * @returns {Object} An object containing configuration for each form field.
 * @returns {Object} return.firstName - Configuration for the first name field.
 * @returns {Object} return.lastName - Configuration for the last name field.
 * @returns {Object} return.email - Configuration for the email field.
 * @returns {Object} return.stateCode - Configuration for the state code field.
 */
export default function useTrackMyOrderFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const {validateField} = useXSSProtection()

    const {shipToCountry} = useShipToCountry()

    const fields = {
        orderNumber: {
            name: `${prefix}orderNumber`,
            label: formatMessage({
                defaultMessage: 'Order Number',
                id: 'track_my_order_fields.label.order_number'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your order number.',
                    id: 'track_my_order_fields.error.required_order_number'
                }),
                validate: (value) => validateField(toast, value, 'trackmyorder-form', 'ordernumber')
            },
            error: errors[`${prefix}orderNumber`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'track_my_order_fields.label.email'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your email address.',
                    id: 'track_my_order_fields.error.required_email'
                }),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address.',
                        id: 'track_my_order_fields.error.invalid_email'
                    })
                },
                validate: (value) => validateField(toast, value, 'trackmyorder-form', 'email')
            },
            error: errors[`${prefix}email`],
            control
        },
        postalCode: {
            name: `${prefix}postalCode`,
            label: formatMessage(
                shipToCountry?.id === 'CA' ? messages.postalCode : messages.zipCode
            ),
            defaultValue: '',
            type: 'text',
            autoComplete: 'postal-code',
            rules: {
                required:
                    shipToCountry?.id === 'CA'
                        ? 'Please enter your postal code.'
                        : formatMessage({
                              defaultMessage: 'Please enter your zip code.',
                              id: 'track_my_order_fields.error.please_enter_your_postal_or_zip',
                              description:
                                  'Error message for a blank zip code (US-specific checkout)'
                          }),
                validate: (value) => validateField(toast, value, 'trackmyorder-form', 'zipcode')
            },
            error: errors[`${prefix}postalCode`],
            control
        }
    }
    return fields
}
